import { useCallback, useMemo } from 'react';

import type Location from 'types/Locations/Location';

import useGroup from './useGroup';
import useLocation from './useLocation';

export default function useGroupedLocations() {
  const {
    groups,
    selectedGroup,
    selectedMultiGroups,
    setGroup,
    isGroupSelected,
    toggleMultiGroup: baseToggleMultiGroup,
  } = useGroup();
  const {
    locations,
    selectedLocation,
    selectedMultiLocations,
    setLocation,
    setLocations,
    toggleMultiLocation,
    isLocationSelected,
  } = useLocation();

  const selectedGroupIds = useMemo(() => {
    const groupIds = selectedMultiGroups.map((g) => g.id);
    return new Set(groupIds);
  }, [selectedMultiGroups]);

  const groupIdToName = useMemo(() => (
    Object.fromEntries(selectedMultiGroups.map((g) => [g.id, g.name]))
  ), [selectedMultiGroups]);

  const availableLocations = locations?.filter((l) => l.groupId === selectedGroup?.id) || [];
  const availableMultiGroupLocations: Location[] = useMemo(() => (
    locations
      ?.filter((l) => selectedGroupIds.has(l.groupId))
      ?.sort((a, b) => a.groupId - b.groupId) || []
  ), [locations, selectedGroupIds]);

  const groupedMultiLocations = useMemo(() => {
    const groupMap = Object.fromEntries(selectedMultiGroups.map((g) => [g.id, [] as Location[]]));
    return availableMultiGroupLocations.reduce((acc, l) => {
      if (l.groupId in acc) acc[l.groupId].push(l);
      else acc[l.groupId] = [l];
      return acc;
    }, groupMap);
  }, [selectedMultiGroups, availableMultiGroupLocations]);

  const toggleMultiGroup = useCallback((id: number) => {
    const wasIncluded = baseToggleMultiGroup(id);

    if (wasIncluded) {
      const groupLocations = locations.filter((l) => l.groupId === id);
      const newLocations = selectedMultiLocations.concat(groupLocations);
      setLocations(newLocations);
    } else {
      const newLocations = selectedMultiLocations.filter((l) => l.groupId !== id);
      setLocations(newLocations);
    }

    return wasIncluded;
  }, [locations, baseToggleMultiGroup, selectedMultiLocations, setLocations]);

  return {
    groups,
    selectedGroup,
    selectedMultiGroups,
    setGroup,
    isGroupSelected,
    toggleMultiGroup,
    availableLocations,
    groupedMultiLocations,
    groupIdToName,
    locations,
    selectedLocation,
    selectedMultiLocations,
    setLocation,
    toggleMultiLocation,
    isLocationSelected,
  };
}
